import React, { useState, useEffect } from 'react';
import {
    Send, ThumbsUp, ThumbsDown, ShieldCheck, XCircle, MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Toast, { type ToastMessage } from '../common/Toast';
import { Task, TaskStatus, tasksService } from '../../services/tasksService';
import {
    governanceMeetingsService, meetingAgendaService,
    GovernanceMeeting, MeetingAgendaItem,
} from '../../services/governanceService';
import { FolderKanban, CalendarClock, MapPin, User } from 'lucide-react';
import { statusBadgeCss, severityBadgeCss } from '../../utils/statusVariant';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

interface Props {
    task: Task;
    isOpen: boolean;
    onClose: () => void;
    onMutated: (updated: Task) => void;
    /** History/archive view — render purely read-only: no lifecycle actions. */
    readOnly?: boolean;
    /** Unused legacy prop — kept so existing callers don't need updating. */
    onEditPlan?: (task: Task) => void;
}

const STATUS_LABELS: Partial<Record<TaskStatus, string>> = {
    OPEN: 'Open',
    IN_PROGRESS: 'In Progress',
    VERIFIED: 'Pending Ratification',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
    // legacy / backward-compat values
    PENDING: 'Pending Ratification',
    ARCHIVED: 'Done (archived)',
    REVERTED: 'Reverted',
    REJECTED: 'Rejected',
};

const TERMINAL: TaskStatus[] = ['DONE', 'ARCHIVED', 'CANCELLED', 'REJECTED'];

// Actions that need a remarks compose panel before the API call fires.
type ActionKey = 'submit' | 'approve' | 'reject' | 'ratify';

const ACTION_META: Record<ActionKey, {
    label: string;
    icon: LucideIcon;
    variant: 'fill' | 'outline' | 'danger';
    placeholder: string;
    heading: string;
}> = {
    submit: {
        label: 'Submit',
        icon: Send,
        variant: 'fill',
        placeholder: 'Describe what you did and how the task is complete…',
        heading: 'Submit with remarks (required)',
    },
    approve: {
        label: 'Approve',
        icon: ThumbsUp,
        variant: 'fill',
        placeholder: 'Your sign-off remarks…',
        heading: 'Approve with remarks (required)',
    },
    reject: {
        label: 'Reject',
        icon: ThumbsDown,
        variant: 'outline',
        placeholder: 'Explain why this is being rejected so the doer can rework it…',
        heading: 'Reject with reason (required) — task returns to Open',
    },
    ratify: {
        label: 'Ratify',
        icon: ShieldCheck,
        variant: 'fill',
        placeholder: 'Your ratification remarks…',
        heading: 'Ratify with remarks (required)',
    },
};

// ── Notes timeline helpers ────────────────────────────────────────────────────

interface RemarkEntry { role: string; date: string; text: string; }

function parseRemarks(raw?: string | null): RemarkEntry[] {
    if (!raw?.trim()) return [];
    return raw.split('\n\n---\n\n').map(entry => {
        const lines = entry.trim().split('\n');
        const match = lines[0].match(/^\[(.+?) \| (.+?)\]$/);
        if (match) return { role: match[1], date: match[2], text: lines.slice(1).join('\n').trim() };
        return { role: 'Note', date: '', text: entry.trim() };
    });
}

function remarkStyle(role: string): React.CSSProperties {
    if (role.includes('Rejected'))
        return { borderLeft: '3px solid #fcd34d', background: '#fffbeb' };
    if (role === 'Doer')
        return { borderLeft: '3px solid #93c5fd', background: '#eff6ff' };
    if (role === 'Approver')
        return { borderLeft: '3px solid #6ee7b7', background: '#f0fdf4' };
    if (role === 'Ratifier')
        return { borderLeft: '3px solid #a78bfa', background: '#f5f3ff' };
    return { borderLeft: '3px solid #e5e7eb', background: '#f9fafb' };
}

const RemarksTimeline: React.FC<{ raw?: string | null }> = ({ raw }) => {
    const entries = parseRemarks(raw);
    // Compute before the hook so useState is always called unconditionally.
    const latestIsRejection = entries.length > 0 && entries[entries.length - 1].role.includes('Rejected');
    const [open, setOpen] = React.useState(latestIsRejection);
    if (!entries.length) return null;
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: '#6b7280',
                    background: 'none', border: 'none', padding: 0,
                }}
            >
                <MessageSquare size={12} />
                Notes ({entries.length})
                {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {open && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {entries.map((r, i) => (
                        <div key={i} style={{
                            ...remarkStyle(r.role),
                            borderRadius: '0 6px 6px 0',
                            padding: '8px 10px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{r.role}</span>
                                {r.date && <span style={{ fontSize: 10, color: '#9ca3af' }}>{r.date}</span>}
                            </div>
                            <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{r.text}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const TaskActionPanel: React.FC<Props> = ({
    task, isOpen, onClose, onMutated, readOnly = false,
}) => {
    const { formatDate } = useEntitySettings();
    // Always re-fetch the latest task from the backend when the panel opens so
    // the panel never shows stale status / role from a cached task list.
    const [liveTask, setLiveTask] = useState<Task>(task);
    useEffect(() => {
        if (!isOpen) return;
        setLiveTask(task); // show the cached version immediately
        tasksService.get(task.id).then(setLiveTask).catch(() => {}); // then refresh
    }, [isOpen, task.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // A task can be ABOUT a meeting (registry type_code 'MEETING'); pull the
    // meeting + its agenda so the panel shows what the meeting actually is.
    const meetingId =
        liveTask.linked_entities?.find(l => l.type_code === 'MEETING')?.record_id ??
        (liveTask.source_type_code === 'MEETING' ? liveTask.source_record_id : null) ??
        (liveTask.linked_type_code === 'MEETING' ? liveTask.linked_record_id : null) ??
        null;
    const [meeting, setMeeting] = useState<GovernanceMeeting | null>(null);
    const [agenda, setAgenda] = useState<MeetingAgendaItem[]>([]);
    useEffect(() => {
        if (!isOpen || !meetingId) { setMeeting(null); setAgenda([]); return; }
        let cancelled = false;
        governanceMeetingsService.getById(meetingId).then(m => { if (!cancelled) setMeeting(m); }).catch(() => { if (!cancelled) setMeeting(null); });
        meetingAgendaService.getByMeeting(meetingId).then(a => { if (!cancelled) setAgenda(a); }).catch(() => { if (!cancelled) setAgenda([]); });
        return () => { cancelled = true; };
    }, [isOpen, meetingId]);

    const role = liveTask.my_role;
    const status = liveTask.status;
    const isTerminal = TERMINAL.includes(status);

    // Pre-compute available actions so we can auto-open the compose panel when
    // there is exactly one action (e.g. DOER+OPEN always shows just "submit").
    const computeAvailableActions = (): ActionKey[] => {
        if (readOnly || isTerminal) return [];
        // REVERTED is a legacy status treated as OPEN for the doer (re-submit path)
        if (role === 'DOER' && (status === 'OPEN' || status === 'REVERTED')) return ['submit'];
        if (role === 'APPROVER' && status === 'IN_PROGRESS') return ['approve', 'reject'];
        if (role === 'VALIDATOR' && (status === 'PENDING' || status === 'VERIFIED')) return ['ratify', 'reject'];
        return [];
    };
    const availableActions = computeAvailableActions();

    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [activeAction, setActiveAction] = useState<ActionKey | null>(
        availableActions.length === 1 ? availableActions[0] : null
    );
    const [remarks, setRemarks] = useState('');

    const run = async (fn: () => Promise<Task>) => {
        setBusy(true);
        setToast(null);
        try {
            const updated = await fn();
            onMutated(updated);
            setActiveAction(null);
            setRemarks('');
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Action failed' });
        } finally {
            setBusy(false);
        }
    };

    const fireAction = () => {
        const r = remarks.trim();
        if (!r) return;
        switch (activeAction) {
            case 'submit': return run(() => tasksService.submit(liveTask.id, r));
            case 'approve': return run(() => tasksService.approve(liveTask.id, r));
            case 'reject': return run(() => tasksService.reject(liveTask.id, r));
            case 'ratify': return run(() => tasksService.ratify(liveTask.id, r));
        }
    };

    const cancelAction = () => { setActiveAction(null); setRemarks(''); };

    return (
        <Modal data-testid="MOD_task_action_panel" isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>{liveTask.name}</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col gap-4 text-sm">
                    {/* Status + role + priority badges */}
                    <div className="flex items-center flex-wrap gap-2">
                        <span className={statusBadgeCss(status, 'sm')}>
                            {STATUS_LABELS[status] ?? status}
                        </span>
                        {role && (
                            <span className="status-badge sm info">
                                Your role: {role.replace(/_/g, ' ')}
                            </span>
                        )}
                        {liveTask.priority && (
                            <span className={severityBadgeCss(liveTask.priority, 'sm')}>
                                {liveTask.priority.replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                        )}
                    </div>

                    {/* Project context — what project this task belongs to. */}
                    {(liveTask.project_name || liveTask.project_id) && (
                        <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-violet-700 uppercase tracking-wider mb-1">
                                <FolderKanban size={13} /> Project
                            </div>
                            <div className="text-sm font-semibold text-gray-800">{liveTask.project_name || `Project #${liveTask.project_id}`}</div>
                            {liveTask.wbs_code && <div className="text-xs text-gray-500 mt-0.5">WBS {liveTask.wbs_code}</div>}
                        </div>
                    )}

                    {/* Origin — the record this task was raised from (CAPA, audit, etc.). */}
                    {liveTask.source_label && liveTask.source_type_code !== 'MEETING' && (
                        <div className="text-xs text-gray-600">
                            <span className="font-semibold text-gray-500 uppercase mr-2">Origin</span>
                            <span className="status-badge xs neutral mr-1">{liveTask.source_type_code}</span>
                            {liveTask.source_label}
                        </div>
                    )}

                    {liveTask.description && (
                        <div className="text-gray-700 whitespace-pre-wrap">{liveTask.description}</div>
                    )}

                    {/* Meeting context — full meeting details + agenda when this is a meeting task. */}
                    {meeting && (
                        <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 uppercase tracking-wider mb-1">
                                <CalendarClock size={13} /> Meeting
                            </div>
                            <div className="text-sm font-semibold text-gray-800">{meeting.meeting_title}</div>
                            <div className="text-xs text-gray-600 mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                                <span className="flex items-center gap-1">
                                    <CalendarClock size={12} />
                                    {meeting.scheduled_date}
                                    {meeting.scheduled_time_from && ` · ${meeting.scheduled_time_from}${meeting.scheduled_time_to ? `–${meeting.scheduled_time_to}` : ''}`}
                                </span>
                                {meeting.location && <span className="flex items-center gap-1"><MapPin size={12} />{meeting.location}</span>}
                                {meeting.chairman_name && <span className="flex items-center gap-1"><User size={12} />{meeting.chairman_name}</span>}
                                {meeting.meeting_status && <span className="status-badge xs neutral">{meeting.meeting_status}</span>}
                            </div>
                            {meeting.meeting_description && (
                                <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{meeting.meeting_description}</div>
                            )}
                            <div className="mt-2">
                                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Agenda</div>
                                {agenda.length === 0 ? (
                                    <div className="text-xs text-gray-400 italic">No agenda items.</div>
                                ) : (
                                    <ol className="list-decimal list-inside text-sm text-gray-700 space-y-0.5">
                                        {agenda.map(a => (
                                            <li key={a.id}>
                                                {a.agenda_name}
                                                {a.agenda_description && <span className="text-xs text-gray-500"> — {a.agenda_description}</span>}
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Meta grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600">
                        {liveTask.due_date && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Due</span>
                                <div>{formatDate(liveTask.due_date)}{liveTask.grace_period_days > 0 && ` (+ ${liveTask.grace_period_days}d grace)`}</div>
                            </div>
                        )}
                        {liveTask.doer_name && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Doer</span>
                                <div>{liveTask.doer_name}</div>
                            </div>
                        )}
                        {liveTask.approver_name && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Approver</span>
                                <div>{liveTask.approver_name}</div>
                            </div>
                        )}
                        {liveTask.validator_name && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Ratifier</span>
                                <div>{liveTask.validator_name}</div>
                            </div>
                        )}
                        {liveTask.created_by_name && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Created By</span>
                                <div>{liveTask.created_by_name}</div>
                            </div>
                        )}
                        {liveTask.created_on && (
                            <div>
                                <span className="font-semibold text-gray-500 uppercase">Created On</span>
                                <div>{formatDate(liveTask.created_on)}</div>
                            </div>
                        )}
                    </div>

                    {/* Linked entities */}
                    {((liveTask.linked_entities && liveTask.linked_entities.length > 0) || liveTask.linked_label) && (
                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Linked to</div>
                            <div className="flex flex-wrap gap-2">
                                {liveTask.linked_entities && liveTask.linked_entities.length > 0
                                    ? liveTask.linked_entities.map((l, i) => (
                                        <span key={l.id ?? `${l.type_id}-${l.record_id}-${i}`}
                                            className="status-badge xs info gap-2">
                                            <span className="text-[10px] font-bold uppercase tracking-wider">{l.type_code}</span>
                                            {l.code && <span className="text-[11px] opacity-75">{l.code}</span>}
                                            <span className="font-medium">{l.label}</span>
                                        </span>
                                    ))
                                    : (
                                        <span className="status-badge xs info gap-2">
                                            <span className="text-[10px] font-bold uppercase tracking-wider">{liveTask.linked_type_code}</span>
                                            <span className="font-medium">{liveTask.linked_label}</span>
                                        </span>
                                    )}
                            </div>
                        </div>
                    )}

                    {/* Notes timeline — all remarks from every workflow step */}
                    <RemarksTimeline raw={liveTask.revert_notes} />

                    {/* Inline remarks compose — shown when an action is selected */}
                    {activeAction && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                {ACTION_META[activeAction].heading}
                            </label>
                            <textarea
                                data-testid={`INP_task_action_${activeAction}_remarks`}
                                autoFocus
                                value={remarks}
                                onChange={e => setRemarks(e.target.value)}
                                rows={3}
                                className="w-full p-2 text-sm border border-gray-300 rounded resize-y"
                                placeholder={ACTION_META[activeAction].placeholder}
                            />
                            <div className="flex justify-end gap-2">
                                <Button
                                    data-testid={`BTN_task_action_${activeAction}_cancel`}
                                    variant="outline"
                                    onClick={cancelAction}
                                    disabled={busy}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid={`BTN_task_action_${activeAction}_confirm`}
                                    variant={ACTION_META[activeAction].variant}
                                    icon={ACTION_META[activeAction].icon}
                                    onClick={fireAction}
                                    disabled={busy || !remarks.trim()}
                                >
                                    {ACTION_META[activeAction].label}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Waiting state — user has a role but no action right now */}
                    {!readOnly && !isTerminal && availableActions.length === 0 && !activeAction && role && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 flex gap-2 items-start">
                            <span style={{ fontSize: 16, lineHeight: 1.2 }}>⏳</span>
                            <span>
                                {role === 'DOER' && status === 'IN_PROGRESS' && 'You\'ve submitted this task. Waiting for the Approver to review.'}
                                {role === 'DOER' && (status === 'VERIFIED' || status === 'PENDING') && 'This task is with the Ratifier for final sign-off.'}
                                {role === 'APPROVER' && status === 'OPEN' && 'Waiting for the Maker to submit their work.'}
                                {role === 'APPROVER' && (status === 'VERIFIED' || status === 'PENDING') && 'You\'ve approved this task. Waiting for the Ratifier to confirm.'}
                                {role === 'VALIDATOR' && status === 'OPEN' && 'Waiting for the Maker to submit their work.'}
                                {role === 'VALIDATOR' && status === 'IN_PROGRESS' && 'Waiting for the Approver to review before you can ratify.'}
                                {role === 'CREATOR' && !isTerminal && 'This task is being worked on. You can cancel it if needed.'}
                            </span>
                        </div>
                    )}

                    {isTerminal && !readOnly && (
                        <div className="text-xs text-gray-500 italic">
                            This task is complete. No further actions are available.
                        </div>
                    )}

                </div>
            </Modal.Body>

            {/* Footer action buttons — only shown when no compose panel is open */}
            {!activeAction && !readOnly && (availableActions.length > 0 || liveTask.is_creator) && (
                <Modal.Footer>
                    <div className="flex justify-end gap-2 w-full">
                        {availableActions.map(a => (
                            <Button
                                key={a}
                                data-testid={`BTN_task_action_${a}_open`}
                                variant={ACTION_META[a].variant}
                                icon={ACTION_META[a].icon}
                                onClick={() => setActiveAction(a)}
                                disabled={busy}
                            >
                                {ACTION_META[a].label}
                            </Button>
                        ))}
                        {liveTask.is_creator && !isTerminal && (
                            <Button
                                key="cancel"
                                data-testid="BTN_task_action_cancel"
                                variant="outline"
                                icon={XCircle}
                                onClick={() => run(() => tasksService.cancel(liveTask.id))}
                                disabled={busy}
                            >
                                Cancel Task
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            )}
            <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

export default TaskActionPanel;
